// Copyright (c) 2026 Query Health Inc. All rights reserved.
// Author: Shubham Dixit <shubham@quantinohealth.com>
// Licensed under a Commercial License. See LICENSE for terms.

import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import {
  ArrowLeft,
  Phone,
  Mail,
  MapPin,
  CreditCard,
  User,
  Stethoscope,
  Pill,
  AlertTriangle,
  FlaskConical,
  Syringe,
  Activity,
  Brain,
  FileText,
  Clock,
  Send,
  CheckCircle2,
  Loader2,
  RefreshCw,
  RotateCcw,
  Tablet,
  Globe,
  ListChecks,
  GitBranch,
  Database,
  ClipboardList,
  Sparkles,
  Copy,
  History,
  Droplet,
  ChevronDown,
  ChevronRight,
  CalendarClock,
  MessageSquare,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { PinInput } from "@/components/PinInput";
import { SnapshotView } from "@/components/SnapshotView";
import { PREP_STATUS_LABEL } from "@/types";
import type {
  PatientDetail,
  PrepNote,
  Condition,
  Medication,
  LabResult,
  Allergy,
  Immunization,
  Encounter,
} from "@/types";
import { PersonAvatar } from "@/components/ui/person-avatar";
import { request, ApiError } from "@/lib/api";
import {
  DATE_DASH,
  formatDisplayDateTime,
  formatDobDisplay,
} from "@/lib/date-format";
import { usePatients } from "@/context/patient-store-context";
import { useAuth } from "@/context/auth-context";
import { isClinicalStaff } from "@/lib/access";
import {
  INTAKE_STATUS_TONE,
  apptStatusTone,
  apptStatusKey,
  visitTypeKey,
  titleCaseCode,
  formatApptTime,
  intakeActionLabelKey,
  type IntakeStatus,
  type PatientAppointment,
} from "@/lib/encounter";
import {
  sendSelfEvalIntent,
  resetAppointmentIntake,
  listSupersededAttempts,
  startKiosk,
  buildKioskUrl,
  intentLang,
  regeneratePrepNote,
  getTranscript,
  type TranscriptMessage,
  type SupersededAttempt,
} from "@/lib/intake";
import { usePatientDetail } from "@/hooks/use-patient-detail";

// Encounter-anchored patient detail. Chart = clinical record + past visits;
// Encounters = the appointment cards where intake + prep note live; Snapshot = the
// deterministic AI projection (transparency/QA view).
type ViewKey = "chart" | "encounters" | "snapshot";

export type PullStatus =
  "pending" | "running" | "completed" | "failed" | "no_data" | "skipped_fresh";
type TriggerSource = "manual_refresh" | "intent" | "prewarm";

// Compact latest-pull view embedded on the patient detail.
interface ApiLatestPull {
  status: PullStatus;
  startedAt: string;
  finishedAt?: string;
  fileCount: number;
  errorCode?: string;
}

// One HIE pull attempt from GET /v1/patients/{id}/clinical-pulls.
interface ApiClinicalPull {
  id: string;
  status: PullStatus;
  triggerSource: TriggerSource;
  triggeredBy?: string;
  startedAt: string;
  finishedAt?: string;
  fileCount: number;
  errorCode?: string;
  errorMessage?: string;
}

// ── GET /v1/patients/{patientId} response contract ──
export interface ApiPatientDetail {
  patientId: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  sex?: string;
  gender?: string;
  mrn?: string;
  particlePatientId?: string;
  phone?: string;
  email?: string;
  addressCity?: string;
  addressState?: string;
  postalCode?: string;
  insurance?: string;
  memberId?: string;
  bloodType?: string;
  conditions?: Condition[];
  medications?: Medication[];
  allergies?: Allergy[];
  labs?: LabResult[];
  encounters?: Encounter[];
  immunizations?: Immunization[];
  prepNote?: PatientDetail["prepNote"];
  // Encounters for this patient (worklist row minus patient-identity fields).
  appointments?: PatientAppointment[];
  clinicalDataUpdatedAt?: string | null;
  latestPull?: ApiLatestPull | null;
  // provider/appointment are unset by the backend in the pilot (no appointments
  // feed) → shown only when present, never fabricated.
  provider?: string;
  appointmentDateTime?: string;
  prepStatus?: string;
  // Patient's stored preferred language; seeds the kiosk hand-off picker.
  language?: string;
}

const DASH = DATE_DASH;

// How long the in-note loader waits for the detached enrichment before flipping to a soft
// "use Regenerate" hint. Matches the hook's note-catch-up window.
const ENRICH_HINT_MS = 90 * 1_000;

// prepNoteEnriched reports whether the provider decision-support layer has landed yet
// (any of assessment / differential / plan / HIE / safety flags), distinguishing a fully-generated note
// from a subjective-only one still awaiting the detached enrichment step.
export function prepNoteEnriched(pn?: PrepNote | null): boolean {
  return !!(
    pn &&
    (pn.assessment ||
      pn.differentialDiagnosis?.length ||
      pn.plan?.length ||
      pn.hieFindings?.length ||
      pn.safetyFlags?.length)
  );
}

// Normalize the backend's prep status (in_progress) to the UI's hyphenated form.
function normalizePrepStatus(s?: string): PatientDetail["prepStatus"] {
  const v = (s ?? "unopened").replace("_", "-");
  return (
    ["unopened", "in-progress", "completed", "reviewed"].includes(v)
      ? v
      : "unopened"
  ) as PatientDetail["prepStatus"];
}

// Whole-year age from an ISO date-of-birth; null when unparseable.
function ageFromDob(dob?: string): number | null {
  if (!dob) return null;
  const d = new Date(dob);
  if (Number.isNaN(d.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age--;
  return age >= 0 && age < 200 ? age : null;
}

function titleCase(s?: string): string | null {
  if (!s) return null;
  const v = s.trim();
  if (!v) return null;
  return v.charAt(0).toUpperCase() + v.slice(1).toLowerCase();
}

// Join only the present parts with a separator — never a lone " · " around blanks.
function joinParts(parts: (string | undefined | null)[], sep = " · "): string {
  return parts
    .map((p) => p?.trim())
    .filter(Boolean)
    .join(sep);
}

// Humanize an encounter type: drop bare codes (→ caller falls back), title-case
// single-case strings, and leave already-readable mixed-case text untouched.
function humanizeType(s?: string): string | null {
  if (!s) return null;
  const v = s.trim();
  if (!v || /^[0-9.\-\s]+$/.test(v)) return null;
  if (v === v.toUpperCase() || v === v.toLowerCase()) {
    return v
      .split(/\s+/)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
      .join(" ");
  }
  return v;
}

function flagCategoryLabel(category?: string): string {
  return humanizeType(category?.replace(/_/g, " ")) ?? "Safety concern";
}

// Clinical triage order for safety flags; unknown severities sink to the end.
const FLAG_SEVERITY_ORDER: Record<string, number> = {
  critical: 0,
  high: 1,
  moderate: 2,
  low: 3,
};

function flagSeverityRank(severity?: string): number {
  return FLAG_SEVERITY_ORDER[(severity ?? "").trim().toLowerCase()] ?? 4;
}

function flagSeverityTone(severity?: string): string {
  switch ((severity ?? "").trim().toLowerCase()) {
    case "critical":
      return "bg-red-600 text-white dark:bg-red-500 dark:text-white";
    case "high":
      return "bg-orange-500/15 text-orange-700 dark:text-orange-300";
    case "moderate":
      return "bg-amber-500/15 text-amber-700 dark:text-amber-300";
    case "low":
      return "bg-blue-500/12 text-blue-700 dark:text-blue-300";
    default:
      return "bg-secondary text-muted-foreground";
  }
}

function flagCardTone(severity?: string): string {
  switch ((severity ?? "").trim().toLowerCase()) {
    case "critical":
      return "border-red-600/35 bg-red-500/[0.08]";
    case "high":
      return "border-orange-500/30 bg-orange-500/[0.08]";
    case "moderate":
      return "border-amber-500/30 bg-amber-500/[0.08]";
    case "low":
      return "border-blue-500/25 bg-blue-500/[0.06]";
    default:
      return "border-border bg-secondary/40";
  }
}

// Cap a (pre-sorted) list to `cap` rows with an in-place expand/collapse toggle.
function useCappedList<T>(items: T[], cap: number) {
  const [expanded, setExpanded] = useState(false);
  const capped = items.length > cap;
  const visible = expanded || !capped ? items : items.slice(0, cap);
  return {
    visible,
    expanded,
    capped,
    total: items.length,
    toggle: () => setExpanded((e) => !e),
  };
}

function ShowAllToggle({
  expanded,
  total,
  onToggle,
}: {
  expanded: boolean;
  total: number;
  onToggle: () => void;
}) {
  const { t } = useTranslation();
  return (
    <button
      onClick={onToggle}
      className="mt-2.5 inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
    >
      {expanded
        ? t("patientDetail.showLess")
        : t("patientDetail.showAll", { count: total })}
      <ChevronDown
        className={[
          "h-3.5 w-3.5 transition-transform",
          expanded ? "rotate-180" : "",
        ].join(" ")}
        aria-hidden
      />
    </button>
  );
}

const KIOSK_LANGS = ["en", "es", "hi"] as const;
type KioskLang = (typeof KIOSK_LANGS)[number];

// Map a stored language (locale or code) to a supported kiosk language; default en.
export function toKioskLang(s?: string): KioskLang {
  const v = (s ?? "").toLowerCase().slice(0, 2);
  return (KIOSK_LANGS as readonly string[]).includes(v)
    ? (v as KioskLang)
    : "en";
}

// Compact, localized relative time for the readout ("just now", "5m ago").
function formatAgo(t: TFunction, iso?: string | null): string | null {
  if (!iso) return null;
  const ts = new Date(iso).getTime();
  if (Number.isNaN(ts)) return null;
  const s = Math.max(0, Math.floor((Date.now() - ts) / 1000));
  if (s < 45) return t("patientDetail.agoJustNow");
  const mins = Math.floor(s / 60);
  if (mins < 60) return t("patientDetail.agoMin", { count: mins });
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return t("patientDetail.agoHour", { count: hrs });
  return t("patientDetail.agoDay", { count: Math.floor(hrs / 24) });
}

// Adapt the API payload into the PatientDetail shape the page renders.
// Clinical arrays default to [] so the view never crashes on a sparse payload.
export function adaptPatient(api: ApiPatientDetail): PatientDetail {
  const address = [api.addressCity, api.addressState, api.postalCode]
    .filter(Boolean)
    .join(", ");
  return {
    id: api.patientId,
    name:
      [api.firstName, api.lastName].filter(Boolean).join(" ") ||
      "Unknown Patient",
    dob: formatDobDisplay(api.dateOfBirth),
    provider: api.provider || DASH,
    appointmentDateTime: formatDisplayDateTime(api.appointmentDateTime),
    prepStatus: normalizePrepStatus(api.prepStatus),
    clinicalDataUpdatedAt: api.clinicalDataUpdatedAt ?? null,
    hasActiveConversation: false,
    phone: api.phone || DASH,
    email: api.email || DASH,
    address: address || DASH,
    insurance: api.insurance || DASH,
    memberId: api.memberId || DASH,
    bloodType: api.bloodType || DASH,
    conditions: api.conditions ?? [],
    medications: api.medications ?? [],
    allergies: api.allergies ?? [],
    labs: api.labs ?? [],
    encounters: api.encounters ?? [],
    immunizations: api.immunizations ?? [],
    prepNote: api.prepNote,
  };
}

const SEVERITY_COLOR: Record<Allergy["severity"], string> = {
  mild: "text-amber-600 bg-amber-500/10 dark:text-amber-300",
  moderate: "text-orange-600 bg-orange-500/10 dark:text-orange-300",
  severe: "text-red-600 bg-red-500/10 dark:text-red-300",
};

const FLAG_VALUE: Record<NonNullable<LabResult["flag"]>, string> = {
  high: "text-red-600 dark:text-red-400 font-semibold",
  low: "text-blue-600 dark:text-blue-400 font-semibold",
  normal: "text-foreground",
};

const FLAG_BADGE: Record<NonNullable<LabResult["flag"]>, string> = {
  high: "bg-red-500/10 text-red-600 dark:text-red-400",
  low: "bg-blue-500/10 text-blue-600 dark:text-blue-400",
  normal: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
};

// One dense clinical section: hairline card, icon + title + mono count.
// `collapsible` turns the header into a toggle; the body mounts only while
// open, so content stays available on demand without a wall of text.
function Section({
  icon: Icon,
  title,
  count,
  alert = false,
  collapsible = false,
  defaultOpen = true,
  children,
}: {
  icon: React.ElementType;
  title: string;
  count: number;
  alert?: boolean;
  collapsible?: boolean;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const bodyVisible = !collapsible || open;
  const headerInner = (
    <>
      <Icon
        className={[
          "h-4 w-4 shrink-0",
          alert ? "text-red-600 dark:text-red-400" : "text-primary",
        ].join(" ")}
      />
      <h3 className="text-[13px] font-semibold text-foreground flex-1 text-left">
        {title}
      </h3>
      {count > 0 && (
        <span className="font-mono text-[11px] text-muted-foreground tabular-nums">
          {count}
        </span>
      )}
      {collapsible && (
        <ChevronDown
          className={[
            "h-3.5 w-3.5 text-muted-foreground transition-transform",
            open ? "rotate-180" : "",
          ].join(" ")}
          aria-hidden
        />
      )}
    </>
  );
  const headerClass = [
    "flex items-center gap-2.5 px-4 py-2.5 w-full",
    bodyVisible ? "border-b" : "",
    alert ? "border-red-500/30 bg-red-500/[0.06]" : "border-border",
  ].join(" ");
  return (
    <section
      className={[
        "bg-card border rounded-xl overflow-hidden",
        alert ? "border-red-500/40" : "border-border",
      ].join(" ")}
    >
      {collapsible ? (
        <button
          type="button"
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
          className={[
            headerClass,
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          ].join(" ")}
        >
          {headerInner}
        </button>
      ) : (
        <div className={headerClass}>{headerInner}</div>
      )}
      {bodyVisible && <div className="px-4 py-3">{children}</div>}
    </section>
  );
}

function EmptyLine({ children }: { children: React.ReactNode }) {
  return <p className="text-[13px] text-muted-foreground py-1">{children}</p>;
}

// Quiet disclaimer for AI-generated interpretive sections (differential, assessment).
function DecisionSupportNote({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-2 mb-3 rounded-lg bg-accent/[0.06] border border-accent/15 px-3 py-2">
      <Sparkles className="h-3.5 w-3.5 text-accent shrink-0 mt-0.5" />
      <p className="text-[11px] text-muted-foreground leading-relaxed">
        {children}
      </p>
    </div>
  );
}

function ProblemsList({ conditions }: { conditions: Condition[] }) {
  const { t } = useTranslation();
  // Active problems first — that's what a clinician scans for.
  const sorted = [...conditions].sort(
    (a, b) =>
      (a.status === "active" ? -1 : 1) - (b.status === "active" ? -1 : 1),
  );
  const { visible, expanded, capped, total, toggle } = useCappedList(sorted, 6);
  if (conditions.length === 0)
    return <EmptyLine>{t("patientDetail.noConditions")}</EmptyLine>;
  return (
    <>
      <ul className="divide-y divide-border">
        {visible.map((c, i) => (
          <li
            key={i}
            className="flex items-center justify-between gap-3 py-2 first:pt-0 last:pb-0"
          >
            <div className="min-w-0 flex items-center gap-2.5">
              <span
                className={[
                  "h-1.5 w-1.5 rounded-full shrink-0",
                  c.status === "active"
                    ? "bg-emerald-500"
                    : "bg-muted-foreground/40",
                ].join(" ")}
                aria-hidden
              />
              <div className="min-w-0">
                <p className="text-[13.5px] font-medium text-foreground truncate">
                  {c.name}
                </p>
                {c.onset && (
                  <p className="font-mono text-[11px] text-muted-foreground">
                    {t("patientDetail.onset", {
                      date: formatDisplayDateTime(c.onset),
                    })}
                  </p>
                )}
              </div>
            </div>
            <span
              className={[
                "text-[11px] font-medium px-2 py-0.5 rounded-full shrink-0",
                c.status === "active"
                  ? "bg-emerald-500/12 text-emerald-700 dark:text-emerald-300"
                  : "bg-secondary text-muted-foreground",
              ].join(" ")}
            >
              {c.status === "active"
                ? t("patientDetail.conditionActive")
                : t("patientDetail.conditionResolved")}
            </span>
          </li>
        ))}
      </ul>
      {capped && (
        <ShowAllToggle expanded={expanded} total={total} onToggle={toggle} />
      )}
    </>
  );
}

function MedicationsList({ medications }: { medications: Medication[] }) {
  const { t } = useTranslation();
  const sorted = [...medications].sort(
    (a, b) =>
      (a.status === "active" ? -1 : 1) - (b.status === "active" ? -1 : 1),
  );
  const { visible, expanded, capped, total, toggle } = useCappedList(sorted, 6);
  if (medications.length === 0)
    return <EmptyLine>{t("patientDetail.noMedications")}</EmptyLine>;
  return (
    <>
      <ul className="divide-y divide-border">
        {visible.map((m, i) => {
          const detail = joinParts([m.dose, m.frequency]);
          return (
            <li
              key={i}
              className="flex items-start justify-between gap-3 py-2 first:pt-0 last:pb-0"
            >
              <div className="min-w-0">
                <p className="text-[13.5px] font-medium text-foreground">
                  {m.name}
                </p>
                {detail && (
                  <p className="font-mono text-[11px] text-muted-foreground">
                    {detail}
                  </p>
                )}
              </div>
              <span
                className={[
                  "text-[11px] font-medium px-2 py-0.5 rounded-full shrink-0",
                  m.status === "active"
                    ? "bg-blue-500/12 text-blue-700 dark:text-blue-300"
                    : "bg-secondary text-muted-foreground",
                ].join(" ")}
              >
                {m.status === "active"
                  ? t("patientDetail.medicationActive")
                  : t("patientDetail.medicationInactive")}
              </span>
            </li>
          );
        })}
      </ul>
      {capped && (
        <ShowAllToggle expanded={expanded} total={total} onToggle={toggle} />
      )}
    </>
  );
}

function AllergiesList({ allergies }: { allergies: Allergy[] }) {
  const { t } = useTranslation();
  if (allergies.length === 0)
    return <EmptyLine>{t("patientDetail.noAllergies")}</EmptyLine>;
  const order = { severe: 0, moderate: 1, mild: 2 } as const;
  const sorted = [...allergies].sort(
    (a, b) => order[a.severity] - order[b.severity],
  );
  return (
    <ul className="divide-y divide-border">
      {sorted.map((a, i) => (
        <li
          key={i}
          className="flex items-start justify-between gap-3 py-2 first:pt-0 last:pb-0"
        >
          <div className="min-w-0">
            <p className="text-[13.5px] font-medium text-foreground">
              {a.substance}
            </p>
            <p className="text-[11px] text-muted-foreground">{a.reaction}</p>
          </div>
          <span
            className={[
              "text-[11px] font-semibold px-2 py-0.5 rounded-full shrink-0 capitalize",
              SEVERITY_COLOR[a.severity],
            ].join(" ")}
          >
            {a.severity}
          </span>
        </li>
      ))}
    </ul>
  );
}

function LabsList({ labs }: { labs: LabResult[] }) {
  const { t } = useTranslation();
  const sorted = [...labs].sort((a, b) =>
    (b.date ?? "").localeCompare(a.date ?? ""),
  );
  const { visible, expanded, capped, total, toggle } = useCappedList(sorted, 6);
  if (labs.length === 0)
    return <EmptyLine>{t("patientDetail.noLabs")}</EmptyLine>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[420px] text-[13px]">
        <thead>
          <tr className="border-b border-border text-left">
            <th className="font-mono text-[10px] font-semibold uppercase tracking-wider text-muted-foreground pb-2 pr-4">
              {t("patientDetail.labColTest")}
            </th>
            <th className="font-mono text-[10px] font-semibold uppercase tracking-wider text-muted-foreground pb-2 pr-4">
              {t("patientDetail.labColResult")}
            </th>
            <th className="font-mono text-[10px] font-semibold uppercase tracking-wider text-muted-foreground pb-2 pr-4">
              {t("patientDetail.labColDate")}
            </th>
            <th className="font-mono text-[10px] font-semibold uppercase tracking-wider text-muted-foreground pb-2">
              {t("patientDetail.labColFlag")}
            </th>
          </tr>
        </thead>
        <tbody>
          {visible.map((l, i) => (
            <tr key={i} className="border-b border-border last:border-0">
              <td className="py-2 pr-4 text-foreground">{l.test}</td>
              <td
                className={[
                  "py-2 pr-4 font-mono tabular-nums",
                  l.flag ? FLAG_VALUE[l.flag] : "",
                ].join(" ")}
              >
                {joinParts([l.value, l.unit], " ")}
              </td>
              <td className="py-2 pr-4 font-mono text-muted-foreground">
                {formatDisplayDateTime(l.date)}
              </td>
              <td className="py-2">
                {l.flag && (
                  <span
                    className={[
                      "text-[10px] font-semibold px-1.5 py-0.5 rounded capitalize",
                      FLAG_BADGE[l.flag],
                    ].join(" ")}
                  >
                    {l.flag}
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {capped && (
        <ShowAllToggle expanded={expanded} total={total} onToggle={toggle} />
      )}
    </div>
  );
}

function ImmunizationsList({
  immunizations,
}: {
  immunizations: Immunization[];
}) {
  const { t } = useTranslation();
  const sorted = [...immunizations].sort((a, b) =>
    (b.date ?? "").localeCompare(a.date ?? ""),
  );
  const { visible, expanded, capped, total, toggle } = useCappedList(sorted, 6);
  if (immunizations.length === 0)
    return <EmptyLine>{t("patientDetail.noImmunizations")}</EmptyLine>;
  return (
    <>
      <ul className="divide-y divide-border">
        {visible.map((imm, i) => (
          <li
            key={i}
            className="flex items-center justify-between gap-3 py-2 first:pt-0 last:pb-0"
          >
            <p className="text-[13.5px] font-medium text-foreground">
              {imm.vaccine}
              {imm.dose?.trim() ? (
                <span className="text-muted-foreground font-normal">
                  {" "}
                  · {imm.dose}
                </span>
              ) : null}
            </p>
            {imm.date && (
              <span className="font-mono text-[11px] text-muted-foreground shrink-0">
                {formatDisplayDateTime(imm.date)}
              </span>
            )}
          </li>
        ))}
      </ul>
      {capped && (
        <ShowAllToggle expanded={expanded} total={total} onToggle={toggle} />
      )}
    </>
  );
}

// Secondary demographics — renders only fields that are present (Particle-sourced
// patients often have no phone/email, so we never show a wall of dashes).
function DemographicsBlock({ api }: { api: ApiPatientDetail }) {
  const { t } = useTranslation();
  const address = [api.addressCity, api.addressState, api.postalCode]
    .filter(Boolean)
    .join(", ");
  const items = [
    { icon: Phone, label: t("patientDetail.labelPhone"), value: api.phone },
    { icon: Mail, label: t("patientDetail.labelEmail"), value: api.email },
    { icon: MapPin, label: t("patientDetail.labelAddress"), value: address },
    {
      icon: CreditCard,
      label: t("patientDetail.labelInsurance"),
      value: api.insurance,
    },
    {
      icon: CreditCard,
      label: t("patientDetail.labelMemberId"),
      value: api.memberId,
      mono: true,
    },
    {
      icon: Droplet,
      label: t("patientDetail.labelBloodType"),
      value: api.bloodType,
      mono: true,
    },
    {
      icon: User,
      label: t("patientDetail.labelProvider"),
      value: api.provider,
    },
    // Internal patient UUID is a backend key, not a clinical identifier — never shown in
    // the chart (a raw UUID reads as a bug to a provider). Human matchers are name + DOB.
  ].filter((i) => i.value && i.value !== DASH) as {
    icon: React.ElementType;
    label: string;
    value: string;
    mono?: boolean;
  }[];

  return (
    <section className="bg-card border border-border rounded-xl overflow-hidden">
      <div className="flex items-center gap-2.5 px-4 py-2.5 border-b border-border">
        <User className="h-4 w-4 text-muted-foreground shrink-0" />
        <h3 className="text-[13px] font-semibold text-foreground">
          {t("patientDetail.demographics")}
        </h3>
      </div>
      <div className="px-4 py-3">
        {items.length === 0 ? (
          <EmptyLine>{t("patientDetail.noDemographics")}</EmptyLine>
        ) : (
          <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3">
            {items.map((item) => (
              <div key={item.label} className="flex items-start gap-2 min-w-0">
                <item.icon className="h-3.5 w-3.5 text-muted-foreground mt-0.5 shrink-0" />
                <div className="min-w-0">
                  <dt className="font-mono text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    {item.label}
                  </dt>
                  <dd
                    className={[
                      "text-[13px] text-foreground mt-0.5",
                      item.mono ? "font-mono break-all" : "break-words",
                    ].join(" ")}
                  >
                    {item.value}
                  </dd>
                </div>
              </div>
            ))}
          </dl>
        )}
      </div>
    </section>
  );
}

// Chart tab: the clinical record (conditions/meds/allergies/immunizations/labs),
// demographics, and the patient's HIE past visits folded in as a section.
function ChartView({
  patient,
  api,
}: {
  patient: PatientDetail;
  api: ApiPatientDetail;
}) {
  const { t } = useTranslation();
  const hasSevere = patient.allergies.some((a) => a.severity === "severe");
  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Section
          icon={Stethoscope}
          title={t("patientDetail.conditionsTitle")}
          count={patient.conditions.length}
        >
          <ProblemsList conditions={patient.conditions} />
        </Section>
        <Section
          icon={Pill}
          title={t("patientDetail.medicationsTitle")}
          count={patient.medications.length}
        >
          <MedicationsList medications={patient.medications} />
        </Section>
        <Section
          icon={AlertTriangle}
          title={t("patientDetail.allergiesTitle")}
          count={patient.allergies.length}
          alert={hasSevere}
        >
          <AllergiesList allergies={patient.allergies} />
        </Section>
        <Section
          icon={Syringe}
          title={t("patientDetail.immunizationsTitle")}
          count={patient.immunizations.length}
        >
          <ImmunizationsList immunizations={patient.immunizations} />
        </Section>
      </div>
      <Section
        icon={FlaskConical}
        title={t("patientDetail.labsTitle")}
        count={patient.labs.length}
      >
        <LabsList labs={patient.labs} />
      </Section>
      <DemographicsBlock api={api} />

      {/* Past Visits — historical HIE encounters */}
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-2.5">
          <History className="h-4 w-4 text-primary shrink-0" />
          <h3 className="text-[13px] font-semibold text-foreground flex-1">
            {t("patientDetail.pastVisits")}
          </h3>
          <span className="font-mono text-[11px] text-muted-foreground tabular-nums">
            {patient.encounters.length}
          </span>
        </div>
        <EncountersView encounters={patient.encounters} />
      </div>
    </div>
  );
}

function EncounterCard({ encounter }: { encounter: Encounter }) {
  const { t } = useTranslation();
  const label =
    humanizeType(encounter.type) ?? t("patientDetail.encounterFallback");
  const provider = encounter.provider?.trim();
  const note = encounter.note?.trim();
  const date = encounter.date ? formatDisplayDateTime(encounter.date) : null;
  return (
    <div className="bg-card border border-border rounded-xl p-4 flex flex-col gap-2">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm font-semibold text-foreground">{label}</p>
        {date && (
          <span className="text-[11px] text-muted-foreground shrink-0">
            {date}
          </span>
        )}
      </div>
      {provider && (
        <p className="text-[11px] text-muted-foreground">{provider}</p>
      )}
      {note && (
        <p className="text-[13px] text-foreground/80 leading-relaxed">{note}</p>
      )}
    </div>
  );
}

function EncountersView({ encounters }: { encounters: Encounter[] }) {
  const { t } = useTranslation();
  const sorted = [...encounters].sort((a, b) =>
    (b.date ?? "").localeCompare(a.date ?? ""),
  );
  const { visible, expanded, capped, total, toggle } = useCappedList(
    sorted,
    15,
  );
  if (encounters.length === 0)
    return (
      <div className="flex flex-col items-center gap-3 py-16 text-center">
        <div className="h-12 w-12 rounded-xl bg-secondary flex items-center justify-center">
          <Activity className="h-6 w-6 text-muted-foreground" />
        </div>
        <p className="text-sm text-muted-foreground">
          {t("patientDetail.noEncounters")}
        </p>
      </div>
    );
  return (
    <div className="flex flex-col gap-3">
      {visible.map((enc, i) => (
        <EncounterCard key={i} encounter={enc} />
      ))}
      {capped && (
        <div className="self-center">
          <ShowAllToggle expanded={expanded} total={total} onToggle={toggle} />
        </div>
      )}
    </div>
  );
}

function InClinicView({
  patient,
  appointmentId,
  defaultLanguage,
  secondaryAction,
}: {
  patient: PatientDetail;
  appointmentId?: string;
  defaultLanguage: KioskLang;
  secondaryAction?: React.ReactNode;
}) {
  const { t } = useTranslation();
  const [language, setLanguage] = useState<KioskLang>(defaultLanguage);
  const [pin, setPin] = useState(["1", "2", "3", "4"]);
  const [done, setDone] = useState(false);
  const [launching, setLaunching] = useState(false);
  const [launchError, setLaunchError] = useState<string | null>(null);
  const [lockToken, setLockToken] = useState<string | null>(null);

  const pinComplete = pin.every((d) => d !== "");
  const firstName = patient.name.split(" ")[0] ?? patient.name;

  // Hand off to the tablet: mint a single-use kiosk lock token server-side, then
  // redirect patient-web with the token — never the patient id, never PHI in the URL.
  async function launchKiosk() {
    if (!import.meta.env.VITE_PATIENT_WEB_URL) {
      setLaunchError(t("patientDetail.kioskNotConfigured"));
      return;
    }
    setLaunching(true);
    setLaunchError(null);
    try {
      const res = await startKiosk({
        patientId: patient.id,
        appointmentId,
        language,
      });
      setLockToken(res.lockToken);
      setDone(true);
    } catch (err) {
      setLaunchError(
        err instanceof ApiError && err.status === 403
          ? t("patientDetail.kioskForbidden")
          : t("patientDetail.kioskFailed"),
      );
    } finally {
      setLaunching(false);
    }
  }

  useEffect(() => {
    const patientWebUrl = import.meta.env.VITE_PATIENT_WEB_URL;
    if (!done || !lockToken || !patientWebUrl) return;
    const timer = setTimeout(() => {
      window.location.replace(
        buildKioskUrl(patientWebUrl, lockToken, window.location.href),
      );
    }, 1500);
    return () => clearTimeout(timer);
  }, [done, lockToken]);

  if (done) {
    return (
      <div className="flex flex-col items-center text-center gap-5 py-10 max-w-sm mx-auto">
        <div className="h-16 w-16 rounded-2xl bg-emerald-500/12 flex items-center justify-center">
          <CheckCircle2 className="h-8 w-8 text-emerald-600 dark:text-emerald-400" />
        </div>
        <div>
          <h3 className="text-lg font-bold text-foreground">
            {t("patients.handoff.successTitle")}
          </h3>
          <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">
            {t("patients.handoff.successBody", { name: firstName })}
          </p>
        </div>
        <div className="w-full bg-secondary border border-border rounded-xl px-4 py-3 flex items-center gap-3">
          <Tablet className="h-4 w-4 text-primary shrink-0" />
          <p className="text-xs text-muted-foreground">
            {t("patients.handoff.kioskNote")}
          </p>
        </div>
        <Button
          variant="outline"
          size="default"
          className="self-center"
          onClick={() => {
            setPin(["1", "2", "3", "4"]);
            setLanguage(defaultLanguage);
            setLockToken(null);
            setDone(false);
          }}
        >
          {t("patients.handoff.cancelHandoff", "Cancel hand-off")}
        </Button>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-border bg-secondary/35 p-3">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-[minmax(0,1fr)_minmax(280px,0.82fr)]">
        <div className="rounded-xl border border-border bg-card p-3 shadow-sm sm:p-4">
          <div className="mb-3 flex items-start gap-3">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10">
              <Globe className="h-4 w-4 text-primary" />
            </span>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-foreground">
                {t("patients.handoff.language")}
              </p>
              <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                {t("patientDetail.kioskLanguageHint")}{" "}
                {t("patientDetail.kioskLanguagePatientHint")}
              </p>
            </div>
          </div>
          <Select
            value={language}
            onValueChange={(v) => setLanguage(v as KioskLang)}
          >
            <SelectItem value="en">{t("patients.handoff.english")}</SelectItem>
            <SelectItem value="es">{t("patients.handoff.spanish")}</SelectItem>
            <SelectItem value="hi">{t("patients.handoff.hindi")}</SelectItem>
          </Select>
        </div>

        <div className="rounded-xl border border-border bg-card p-3 shadow-sm sm:p-4">
          <div className="mb-3 flex items-start gap-3">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10">
              <Tablet className="h-4 w-4 text-primary" />
            </span>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-foreground">
                {t("patients.handoff.pinLabel")}
              </p>
              <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                {t("patients.handoff.pinHint")}
              </p>
            </div>
          </div>
          <PinInput value={pin} onChange={setPin} />
        </div>
      </div>

      {launchError && (
        <div className="px-4 py-3 rounded-xl bg-destructive/10 border border-destructive/20">
          <span className="text-sm text-destructive">{launchError}</span>
        </div>
      )}
      <div className="mt-3 flex flex-wrap justify-end gap-2">
        {secondaryAction}
        <Button
          size="default"
          className="w-full sm:w-auto"
          disabled={!pinComplete || launching}
          onClick={() => void launchKiosk()}
        >
          {launching ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Tablet className="h-4 w-4" />
          )}
          {launching
            ? t("patientDetail.kioskStarting")
            : t("patients.handoff.lockButton")}
        </Button>
      </div>
    </div>
  );
}

// Renders one encounter's own prep note (fed per-encounter, never the patient-level
// latest). patientName is display-only; prepStatus drives the reviewed toggle.
function PrepNoteView({
  prepNote,
  patientName,
  prepStatus,
  onReview,
}: {
  prepNote: PrepNote;
  patientName: string;
  prepStatus: PatientDetail["prepStatus"];
  onReview: () => void;
}) {
  const { t } = useTranslation();
  const [reviewed, setReviewed] = useState(prepStatus === "reviewed");
  // A reviewed note collapses to the confirmation panel; "view again" reopens
  // it read-only — Mark Reviewed only exists while the note is unreviewed.
  const [showNote, setShowNote] = useState(prepStatus !== "reviewed");

  // A just-completed note arrives subjective-only; its decision-support layer lands ~40s
  // later via a detached step. Show an in-note loader until it surfaces (the page silently
  // refetches meanwhile), flipping to a soft Regenerate hint if it never does.
  const enriched = prepNoteEnriched(prepNote);
  const [enrichTimedOut, setEnrichTimedOut] = useState(false);
  useEffect(() => {
    if (enriched || prepStatus !== "completed") {
      setEnrichTimedOut(false);
      return;
    }
    const to = setTimeout(() => setEnrichTimedOut(true), ENRICH_HINT_MS);
    return () => clearTimeout(to);
  }, [enriched, prepStatus]);

  function markReviewed() {
    setReviewed(true);
    setShowNote(false);
    onReview();
  }

  if (!showNote) {
    return (
      <div className="flex flex-col items-center gap-4 py-16 text-center">
        <div className="h-14 w-14 rounded-2xl bg-emerald-500/12 flex items-center justify-center">
          <CheckCircle2 className="h-7 w-7 text-emerald-600 dark:text-emerald-400" />
        </div>
        <div>
          <p className="text-base font-semibold text-foreground">
            {t("patientDetail.prepNoteReviewed")}
          </p>
          <p className="text-sm text-muted-foreground mt-1">
            {t("patientDetail.prepNoteReviewedBody")}
          </p>
        </div>
        <button
          onClick={() => setShowNote(true)}
          className="text-xs text-primary hover:underline mt-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
        >
          {t("patientDetail.viewPrepNoteAgain")}
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="bg-card border border-border rounded-xl px-5 py-4 flex items-start justify-between gap-4">
        <div>
          <div className="inline-flex items-center gap-1.5 bg-accent/10 border border-accent/20 rounded-full px-2.5 py-1 mb-2">
            <Brain className="h-3.5 w-3.5 text-accent" />
            <span className="text-[11px] font-semibold text-accent">
              {t("patientDetail.aiGenerated")}
            </span>
          </div>
          <h3 className="text-lg font-bold text-foreground">
            {t("patientDetail.visitPrepNote")}
          </h3>
          <p className="text-muted-foreground text-[13px] mt-0.5">
            {patientName}
          </p>
        </div>
        {/* The one highlighted action in the note — everything else is outline.
            Only offered while the note is still unreviewed. */}
        {!reviewed && (
          <Button size="sm" onClick={markReviewed}>
            <CheckCircle2 className="h-3.5 w-3.5" />
            {t("patientDetail.markReviewed")}
          </Button>
        )}
      </div>

      <Section
        icon={FileText}
        title={t("patientDetail.chiefComplaint")}
        count={0}
      >
        <p className="text-sm text-foreground leading-relaxed">
          {prepNote.chiefComplaint}
        </p>
      </Section>

      {/* Subjective narrative — the longest text in the note. Once the
          decision-support layer has landed it starts collapsed (headers only)
          so the enriched sections lead; every word stays one click away. */}
      {prepNote.sections && prepNote.sections.length > 0 ? (
        // Structured subjective breakdown (the same sections the patient reviewed).
        [...prepNote.sections]
          .sort((a, b) => a.displayOrder - b.displayOrder)
          .map((s) => (
            <Section
              key={s.sectionKey}
              icon={FileText}
              title={s.title || t("patientDetail.aiClinicalSummary")}
              count={0}
              collapsible
              defaultOpen={!enriched}
            >
              <p className="text-sm text-foreground leading-relaxed whitespace-pre-line">
                {s.content}
              </p>
            </Section>
          ))
      ) : (
        <Section
          icon={FileText}
          title={t("patientDetail.aiClinicalSummary")}
          count={0}
          collapsible
          defaultOpen={!enriched}
        >
          <p className="text-sm text-foreground leading-relaxed">
            {prepNote.summary}
          </p>
        </Section>
      )}

      <div className="flex items-center gap-1.5 px-1">
        <Clock className="h-3 w-3 text-muted-foreground" />
        <p className="font-mono text-[11px] text-muted-foreground">
          {t("patientDetail.generatedAt", {
            date: formatDisplayDateTime(prepNote.generatedAt),
          })}
        </p>
      </div>

      {prepNote.safetyFlags && prepNote.safetyFlags.length > 0 && (
        <Section
          icon={AlertTriangle}
          title={t("patientDetail.flagsAlerts")}
          count={prepNote.safetyFlags.length}
          alert
        >
          <DecisionSupportNote>
            {t("patientDetail.flagsAlertsDisclaimer")}
          </DecisionSupportNote>
          <div className="flex flex-col gap-2.5">
            {/* Triage order: critical → high → moderate → low (stable within a tier). */}
            {[...prepNote.safetyFlags]
              .sort(
                (a, b) =>
                  flagSeverityRank(a.severity) - flagSeverityRank(b.severity),
              )
              .map((flag, i) => (
              <div
                key={i}
                className={[
                  "rounded-lg border px-3.5 py-3",
                  flagCardTone(flag.severity),
                ].join(" ")}
              >
                <div className="flex items-center gap-2 flex-wrap">
                  <span
                    className={[
                      "text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded",
                      flagSeverityTone(flag.severity),
                    ].join(" ")}
                  >
                    {flag.severity || t("patientDetail.flagSeverityUnknown")}
                  </span>
                  <span className="text-[11px] font-medium text-muted-foreground">
                    {flagCategoryLabel(flag.category)}
                  </span>
                </div>
                <p className="text-sm font-medium text-foreground leading-relaxed mt-1.5">
                  {flag.summary}
                </p>
                {flag.evidence && (
                  <p className="text-xs text-muted-foreground leading-relaxed mt-1">
                    {flag.evidence}
                  </p>
                )}
              </div>
            ))}
          </div>
        </Section>
      )}

      {prepNote.assessment && (
        <Section
          icon={ClipboardList}
          title={t("patientDetail.assessment")}
          count={0}
          collapsible
        >
          <DecisionSupportNote>
            {t("patientDetail.decisionSupportDisclaimer")}
          </DecisionSupportNote>
          <p className="text-sm text-foreground leading-relaxed">
            {prepNote.assessment}
          </p>
        </Section>
      )}

      {prepNote.differentialDiagnosis &&
        prepNote.differentialDiagnosis.length > 0 && (
          <Section
            icon={GitBranch}
            title={t("patientDetail.differentialDiagnosis")}
            count={prepNote.differentialDiagnosis.length}
            collapsible
          >
            <DecisionSupportNote>
              {t("patientDetail.decisionSupportDisclaimer")}
            </DecisionSupportNote>
            <ul className="list-disc space-y-2 pl-5">
              {prepNote.differentialDiagnosis.map((dx, i) => (
                <li key={i} className="text-sm text-foreground leading-relaxed">
                  {dx}
                </li>
              ))}
            </ul>
          </Section>
        )}

      {prepNote.plan && prepNote.plan.length > 0 && (
        <Section
          icon={ListChecks}
          title={t("patientDetail.plan")}
          count={prepNote.plan.length}
          collapsible
        >
          <ul className="flex flex-col gap-2">
            {prepNote.plan.map((step, i) => (
              <li key={i} className="flex items-start gap-2.5">
                <CheckCircle2 className="h-4 w-4 text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5" />
                <p className="text-sm text-foreground leading-relaxed">
                  {step}
                </p>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {prepNote.hieFindings && prepNote.hieFindings.length > 0 && (
        <Section
          icon={Database}
          title={t("patientDetail.hieFindings")}
          count={prepNote.hieFindings.length}
          collapsible
        >
          <div className="flex flex-col gap-2.5">
            {prepNote.hieFindings.map((finding, i) => {
              const findingDate = finding.date
                ? formatDisplayDateTime(finding.date)
                : "";
              return (
                <div
                  key={i}
                  className="rounded-lg border border-border bg-secondary/35 px-3.5 py-3"
                >
                  {findingDate && findingDate !== DASH && (
                    <div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-medium text-muted-foreground">
                      <CalendarClock className="h-3.5 w-3.5" />
                      <span>{findingDate}</span>
                    </div>
                  )}
                  <p className="text-sm text-foreground leading-relaxed">
                    {finding.summary}
                  </p>
                </div>
              );
            })}
          </div>
        </Section>
      )}

      {!enriched && prepStatus === "completed" && (
        <div className="rounded-xl border border-border bg-secondary/40 px-5 py-4">
          {enrichTimedOut ? (
            <div className="flex items-start gap-2.5">
              <AlertTriangle className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
              <div>
                <p className="text-[13px] font-medium text-foreground">
                  {t(
                    "patientDetail.enrichmentStalledTitle",
                    "Clinical decision support didn’t finish generating",
                  )}
                </p>
                <p className="text-[12px] text-muted-foreground mt-0.5">
                  {t(
                    "patientDetail.enrichmentStalledBody",
                    "Use Regenerate above to try again.",
                  )}
                </p>
              </div>
            </div>
          ) : (
            <div className="flex items-start gap-2.5">
              <Loader2 className="h-4 w-4 text-primary shrink-0 mt-0.5 animate-spin" />
              <div>
                <p className="text-[13px] font-medium text-foreground">
                  {t(
                    "patientDetail.enrichmentPendingTitle",
                    "Generating clinical decision support…",
                  )}
                </p>
                <p className="text-[12px] text-muted-foreground mt-0.5">
                  {t(
                    "patientDetail.enrichmentPendingBody",
                    "Assessment, differential diagnosis, and plan will appear here automatically.",
                  )}
                </p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Copyable patient-web self-eval URL for demos where no email/SMS was delivered.
function DeeplinkCopy({ token }: { token: string }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const base = (import.meta.env.VITE_PATIENT_WEB_URL ?? "").replace(/\/$/, "");
  const url = `${base}/eval?token=${encodeURIComponent(token)}`;
  async function copy() {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  }
  return (
    <div className="flex items-center gap-2 bg-secondary border border-border rounded-lg px-3 py-2 self-start max-w-full">
      <span
        className="font-mono text-xs text-muted-foreground truncate max-w-[340px]"
        title={url}
      >
        {url}
      </span>
      <button
        onClick={() => void copy()}
        className="flex items-center gap-1 text-xs font-semibold text-primary hover:underline shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
      >
        {copied ? (
          <CheckCircle2 className="h-3.5 w-3.5" />
        ) : (
          <Copy className="h-3.5 w-3.5" />
        )}
        {copied ? t("patientDetail.copied") : t("patientDetail.copyLink")}
      </button>
    </div>
  );
}

function EvalLinkStatus({
  token,
  expiresAt,
  usedAt,
}: {
  token: string;
  expiresAt?: string;
  usedAt?: string;
}) {
  const { t } = useTranslation();
  const expiry = expiresAt ? new Date(expiresAt) : null;
  const expired =
    !!usedAt || (expiry ? Number.isNaN(expiry.getTime()) || expiry.getTime() <= Date.now() : false);
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <span
          className={[
            "inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold",
            expired
              ? "bg-red-500/12 text-red-600 dark:text-red-400"
              : "bg-emerald-500/12 text-emerald-700 dark:text-emerald-300",
          ].join(" ")}
        >
          {expired
            ? t("patientDetail.linkExpired", "Expired")
            : t("patientDetail.linkActive", "Active")}
        </span>
        {expiry && !Number.isNaN(expiry.getTime()) && (
          <span className="text-[12px] text-muted-foreground">
            {expired
              ? t("patientDetail.linkExpiredAt", {
                  defaultValue: "Expired {{date}}",
                  date: formatDisplayDateTime(expiresAt ?? ""),
                })
              : t("patientDetail.linkExpiresAt", {
                  defaultValue: "Expires {{date}}",
                  date: formatDisplayDateTime(expiresAt ?? ""),
                })}
          </span>
        )}
      </div>
      <DeeplinkCopy token={token} />
    </div>
  );
}

// Ordered intake chat log for one encounter — role-labeled, system lines muted.
// Fetches on mount (only mounts when the expander opens); handles loading/empty/error.
function TranscriptView({
  patientId,
  interactionId,
}: {
  patientId: string;
  interactionId?: string;
}) {
  const { t } = useTranslation();
  const [messages, setMessages] = useState<TranscriptMessage[] | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "error">("loading");

  useEffect(() => {
    let active = true;
    setState("loading");
    setMessages(null);
    (async () => {
      try {
        const res = await getTranscript(patientId, interactionId);
        if (!active) return;
        setMessages(res.messages ?? []);
        setState("ready");
      } catch {
        if (active) setState("error");
      }
    })();
    return () => {
      active = false;
    };
  }, [patientId, interactionId]);

  if (state === "loading") {
    return (
      <div className="flex items-center gap-2 py-8 justify-center text-[13px] text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        {t("patientDetail.transcriptLoading")}
      </div>
    );
  }

  if (state === "error") {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-destructive/20 bg-destructive/10 px-3 py-2.5 text-[13px] text-destructive">
        <AlertTriangle className="h-4 w-4 shrink-0" />
        <span>{t("patientDetail.transcriptError")}</span>
      </div>
    );
  }

  if (!messages || messages.length === 0) {
    return (
      <p className="py-6 text-center text-[13px] text-muted-foreground">
        {t("patientDetail.transcriptEmpty")}
      </p>
    );
  }

  const roleLabel = (role: TranscriptMessage["role"]) =>
    role === "patient"
      ? t("patientDetail.rolePatient")
      : role === "assistant"
        ? t("patientDetail.roleAssistant")
        : t("patientDetail.roleSystem");
  const fmtTime = (iso?: string) => {
    if (!iso) return "";
    const d = new Date(iso);
    return Number.isNaN(d.getTime())
      ? ""
      : d.toLocaleTimeString(undefined, {
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        });
  };

  return (
    <div className="max-h-96 overflow-y-auto rounded-lg border border-border bg-secondary/40 px-3 py-2.5 flex flex-col gap-3">
      {messages.map((m, i) => (
        <div key={i} className={m.role === "system" ? "opacity-60" : ""}>
          <div className="flex items-baseline gap-2">
            <p className="font-mono text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              {roleLabel(m.role)}
            </p>
            {fmtTime(m.createdAt) && (
              <span className="font-mono text-[10px] text-muted-foreground/70 tabular-nums">
                {fmtTime(m.createdAt)}
              </span>
            )}
          </div>
          <p
            className={[
              "text-[13px] leading-relaxed whitespace-pre-wrap mt-0.5",
              m.role === "system"
                ? "text-muted-foreground italic"
                : "text-foreground",
            ].join(" ")}
          >
            {m.content}
          </p>
        </div>
      ))}
    </div>
  );
}

// Human label for the visit type; null when the encounter carries none.
function apptTypeLabel(t: TFunction, appt: PatientAppointment): string | null {
  const typeKey = visitTypeKey(appt.appointmentType);
  return appt.appointmentType
    ? typeKey
      ? t(`visits.visitType.${typeKey}`)
      : titleCaseCode(appt.appointmentType)
    : null;
}

function ApptStatusPill({ status }: { status?: string }) {
  const { t } = useTranslation();
  if (!status) return null;
  return (
    <span
      className={[
        "inline-flex items-center whitespace-nowrap rounded-full px-2.5 py-0.5 text-[11px] font-medium",
        apptStatusTone(status),
      ].join(" ")}
    >
      {t(`visits.apptStatus.${apptStatusKey(status)}`, {
        defaultValue: titleCaseCode(status),
      })}
    </span>
  );
}

function IntakeStatusPill({ status }: { status: IntakeStatus }) {
  const { t } = useTranslation();
  return (
    <span
      className={[
        "inline-flex items-center whitespace-nowrap rounded-full px-2.5 py-0.5 text-[11px] font-medium",
        INTAKE_STATUS_TONE[status],
      ].join(" ")}
    >
      {t(`visits.status.${status}`)}
    </span>
  );
}

// Shared per-encounter body — the per-encounter intake actions (remote link +
// in-clinic kiosk, both stamped with this appointmentId), this encounter's OWN
// prep note (expandable) or a "pending" hint when the intake reached
// prep_ready/reviewed with no note yet, the transcript, and prior attempts.
// Rendered by both AppointmentCard and the Encounters-tab table row.
// intakeStatus state lives in the caller so its header/row pill mirrors the
// optimistic updates the actions here make.
function AppointmentBody({
  appt,
  patient,
  prepStatus,
  defaultLanguage,
  onReview,
  onRefresh,
  intakeStatus,
  setIntakeStatus,
  showSummary = true,
  defaultShowKiosk = false,
}: {
  appt: PatientAppointment;
  patient: PatientDetail;
  prepStatus: PatientDetail["prepStatus"];
  defaultLanguage: KioskLang;
  onReview: () => void;
  onRefresh: () => void;
  intakeStatus: IntakeStatus;
  setIntakeStatus: (s: IntakeStatus) => void;
  showSummary?: boolean;
  // Open the kiosk hand-off immediately for a not-yet-sent intake (the
  // encounter page's Start Intake landing state).
  defaultShowKiosk?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const { isOperator, user } = useAuth();
  // Redo history: prior intakes for this encounter that were superseded. Re-fetched when
  // the intake status changes (a redo flips it to not_sent), so a fresh redo surfaces here.
  useEffect(() => {
    if (!appt.appointmentId) return;
    let cancelled = false;
    listSupersededAttempts(appt.appointmentId)
      .then((a) => {
        if (!cancelled) setPriorAttempts(a);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [appt.appointmentId, intakeStatus]);
  const [linkState, setLinkState] = useState<"idle" | "sending" | "error">(
    "idle",
  );
  const [linkFeedback, setLinkFeedback] = useState<{
    emailSent: boolean;
    deeplinkToken: string;
    expiresAt: string;
  } | null>(null);
  // Operators (qh_admin+) may edit where the deeplink SMS is delivered; auto-filled from
  // the patient's profile phone. Delivery target only — never touches identity binding.
  const [phoneOverride, setPhoneOverride] = useState(
    patient.phone && patient.phone !== DASH ? patient.phone : "",
  );
  const [showKiosk, setShowKiosk] = useState(
    defaultShowKiosk && appt.intakeStatus === "not_sent",
  );
  const [prepOpen, setPrepOpen] = useState(true);
  const [regenState, setRegenState] = useState<"idle" | "running" | "error">(
    "idle",
  );
  const [priorAttempts, setPriorAttempts] = useState<SupersededAttempt[]>([]);
  const [priorOpen, setPriorOpen] = useState(false);
  const [redoOpen, setRedoOpen] = useState(false);
  const [redoReason, setRedoReason] = useState("");
  const [redoState, setRedoState] = useState<"idle" | "redoing" | "error">(
    "idle",
  );

  useEffect(() => {
    if (intakeStatus !== "not_sent") setShowKiosk(false);
  }, [intakeStatus]);

  const resetActor =
    user?.displayName?.trim() || user?.email?.trim() || "unknown user";
  function resetReason(note?: string): string {
    const trimmed = note?.trim();
    return trimmed ? `${resetActor}: ${trimmed}` : resetActor;
  }

  const canResend = intakeStatus === "link_sent" && !!appt.appointmentId;

  // Reset is the universal recovery/testing action: it drives THIS encounter back to
  // not_sent from ANY in-flight state (link_sent, in_progress, prep_ready, reviewed),
  // server-side clearing every live attempt. Reachable in every non-not_sent state.
  const canReset = intakeStatus !== "not_sent" && !!appt.appointmentId;

  // Redo a COMPLETED intake for THIS encounter — supersedes the current attempt (retained
  // as history) and resets the encounter to not_sent so a fresh intake can run. Offered
  // once the intake is prep_ready or reviewed. Optional reason for the audit/history.
  const canRedo =
    (intakeStatus === "prep_ready" || intakeStatus === "reviewed") &&
    !!appt.appointmentId;
  async function redo() {
    if (!appt.appointmentId) return;
    setRedoState("redoing");
    try {
      await resetAppointmentIntake(appt.appointmentId, resetReason(redoReason));
      setIntakeStatus("not_sent");
      setLinkFeedback(null);
      setRedoOpen(false);
      setRedoReason("");
      setRedoState("idle");
      onRefresh();
    } catch {
      setRedoState("error");
    }
  }

  // Regenerate this encounter's prep note from the transcript, then refresh the detail
  // so the fresh note surfaces in place (re-fetch is simplest + always consistent).
  async function regenerate() {
    setRegenState("running");
    try {
      await regeneratePrepNote(patient.id);
      setRegenState("idle");
      onRefresh();
    } catch {
      setRegenState("error");
    }
  }

  // Remote intake for THIS encounter — POST /v1/intents with the appointmentId.
  async function sendLink() {
    setLinkState("sending");
    try {
      const res = await sendSelfEvalIntent({
        patientId: patient.id,
        appointmentId: appt.appointmentId,
        language: intentLang(i18n.language),
        phone:
          isOperator && phoneOverride.trim() ? phoneOverride.trim() : undefined,
      });
      setLinkFeedback(res);
      setIntakeStatus("link_sent");
      setLinkState("idle");
    } catch {
      setLinkState("error");
    }
  }

  const notSent = intakeStatus === "not_sent";
  // Once the intake completed, the status pills + prep note say it all, so the
  // inline Intake block is hidden; Redo/Reset (with its audit reason) restarts it.
  const intakeCompleted =
    intakeStatus === "prep_ready" || intakeStatus === "reviewed";

  // Per-encounter prep note: this encounter's own note, else a "pending" hint when
  // its intake reached prep_ready/reviewed but no note has landed yet.
  const prep = appt.prepNote;
  const prepPending = !prep && intakeCompleted;

  return (
      <div className="px-4 py-3 flex flex-col gap-3">
        {/* Reason + provider — omitted in the table, where columns carry them */}
        {showSummary && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5">
            <div className="min-w-0">
              <p className="font-mono text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                {t("patientDetail.reasonForVisit")}
              </p>
              <p className="text-[13px] text-foreground mt-0.5">
                {appt.reason || DASH}
              </p>
            </div>
            <div className="min-w-0">
              <p className="font-mono text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                {t("patientDetail.labelProvider")}
              </p>
              <p className="text-[13px] text-foreground mt-0.5">
                {appt.providerName || DASH}
              </p>
            </div>
          </div>
        )}

        {/* Intake — hidden once the intake completed (see intakeCompleted above) */}
        {!intakeCompleted && (
        <div className="border-t border-border pt-3">
          <p className="font-mono text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">
            {t("patientDetail.intakeSection")}
          </p>
          {notSent && !showKiosk ? (
            <div className="flex flex-col gap-2">
              {isOperator && (
                <label className="flex flex-col gap-1 max-w-xs">
                  <span className="font-mono text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    {t("patientDetail.deliveryPhone", "Send link to (phone)")}
                  </span>
                  <input
                    type="tel"
                    inputMode="tel"
                    value={phoneOverride}
                    onChange={(e) => setPhoneOverride(e.target.value)}
                    placeholder="+15551234567"
                    className="rounded-md border border-border bg-background px-2.5 py-1.5 text-[13px] text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
                  />
                </label>
              )}
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  size="sm"
                  disabled={linkState === "sending"}
                  onClick={() => void sendLink()}
                >
                  {linkState === "sending" ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4" />
                  )}
                  {linkState === "sending"
                    ? t("patientDetail.selfEvalSending")
                    : t("patientDetail.sendSelfEvalLink")}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setShowKiosk(true)}
                >
                  <Tablet className="h-4 w-4" />
                  {t("patientDetail.startInClinicIntake")}
                </Button>
              </div>
              {linkState === "error" && (
                <span className="inline-flex items-center gap-1 text-xs text-destructive">
                  <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                  {t("patientDetail.selfEvalError")}
                </span>
              )}
            </div>
          ) : showKiosk ? (
            <div className="flex flex-col gap-3">
              <InClinicView
                patient={patient}
                appointmentId={appt.appointmentId}
                defaultLanguage={defaultLanguage}
                secondaryAction={
                  <Button
                    size="default"
                    variant="outline"
                    className="w-full sm:w-auto"
                    disabled={linkState === "sending"}
                    onClick={() => void sendLink()}
                  >
                    {linkState === "sending" ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4" />
                    )}
                    {linkState === "sending"
                      ? t("patientDetail.selfEvalSending")
                      : t("patientDetail.sendSelfEvalLink")}
                  </Button>
                }
              />
              {linkState === "error" && (
                <span className="inline-flex items-center gap-1 text-xs text-destructive">
                  <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                  {t("patientDetail.selfEvalError")}
                </span>
              )}
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-[13px] text-muted-foreground">
                  {t(`visits.status.${intakeStatus}`)}
                </span>
                <div className="flex flex-wrap items-center gap-2">
                  {canResend && (
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={linkState === "sending"}
                      onClick={() => void sendLink()}
                    >
                      {linkState === "sending" ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Send className="h-4 w-4" />
                      )}
                      {linkState === "sending"
                        ? t("patientDetail.selfEvalSending")
                        : t("patientDetail.resendSelfEvalLink", "Resend link")}
                    </Button>
                  )}
                  {canReset && (
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={redoState === "redoing"}
                      onClick={() => setRedoOpen((o) => !o)}
                    >
                      <RotateCcw className="h-4 w-4" />
                      {t("patientDetail.resetIntake", "Reset intake")}
                    </Button>
                  )}
                </div>
              </div>
              {intakeStatus === "link_sent" && (
                <>
                  {linkFeedback && (
                    <span
                      className={[
                        "inline-flex items-center gap-1.5 text-xs font-medium",
                        linkFeedback.emailSent
                          ? "text-emerald-600 dark:text-emerald-400"
                          : "text-amber-600 dark:text-amber-400",
                      ].join(" ")}
                    >
                      {linkFeedback.emailSent ? (
                        <CheckCircle2 className="h-3.5 w-3.5" />
                      ) : (
                        <AlertTriangle className="h-3.5 w-3.5" />
                      )}
                      {linkFeedback.emailSent
                        ? t("patientDetail.selfEvalSent")
                        : t("patientDetail.selfEvalNoEmail")}
                    </span>
                  )}
                  {(linkFeedback?.deeplinkToken || appt.evalLink?.token) ? (
                    <EvalLinkStatus
                      token={linkFeedback?.deeplinkToken || appt.evalLink?.token || ""}
                      expiresAt={linkFeedback?.expiresAt || appt.evalLink?.expiresAt}
                      usedAt={appt.evalLink?.usedAt}
                    />
                  ) : null}
                </>
              )}
              {intakeStatus === "link_sent" && !linkFeedback && !(appt.evalLink?.token) && (
                <span className="text-xs text-muted-foreground">
                  {t(
                    "patientDetail.selfEvalLinkUnavailable",
                    "Link details are unavailable. Resend the link to generate a new one.",
                  )}
                </span>
              )}
            </div>
          )}
        </div>
        )}

        {/* Reset/Redo confirm — one shared panel for the universal reset action,
            triggered from any non-not_sent state (intake row, prep header, or the
            prep-pending block). Wording adapts: "Redo" once the intake completed. */}
        {redoOpen && canReset && (
          <div className="border-t border-border pt-3">
            <div className="flex flex-col gap-2.5 rounded-lg border border-border bg-secondary/50 px-3 py-2.5">
              <div>
                <p className="text-[13px] font-medium text-foreground">
                  {intakeCompleted
                    ? t("patientDetail.redoIntakeTitle", "Redo this intake?")
                    : t("patientDetail.resetIntakeTitle", "Reset this intake?")}
                </p>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  {intakeCompleted
                    ? t(
                        "patientDetail.redoIntakeBody",
                        "The current intake and its note are kept as a prior attempt. The encounter resets so you can send a new link or start a kiosk hand-off.",
                      )
                    : t(
                        "patientDetail.resetIntakeBody",
                        "The current attempt is cleared and the encounter returns to Not sent, so you can send a new link or start a kiosk hand-off.",
                      )}
                </p>
              </div>
              <input
                type="text"
                value={redoReason}
                onChange={(e) => setRedoReason(e.target.value)}
                placeholder={t(
                  "patientDetail.redoReasonPlaceholder",
                  "Reason (optional) — e.g. transcript failure, inaccurate note",
                )}
                className="w-full rounded-md border border-border bg-background px-2.5 py-1.5 text-xs text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              />
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={redoState === "redoing"}
                  onClick={() => void redo()}
                >
                  {redoState === "redoing" ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <RotateCcw className="h-3.5 w-3.5" />
                  )}
                  {intakeCompleted
                    ? t("patientDetail.redoIntakeConfirm", "Redo intake")
                    : t("patientDetail.resetIntake", "Reset intake")}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={redoState === "redoing"}
                  onClick={() => {
                    setRedoOpen(false);
                    setRedoReason("");
                  }}
                >
                  {t("patientDetail.cancel", "Cancel")}
                </Button>
              </div>
              {redoState === "error" && (
                <span className="inline-flex items-center gap-1 text-xs text-destructive">
                  <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                  {intakeCompleted
                    ? t(
                        "patientDetail.redoIntakeError",
                        "Could not redo. Please try again.",
                      )
                    : t(
                        "patientDetail.resetIntakeError",
                        "Could not reset. Please try again.",
                      )}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Prep Note — accordion card: title toggles; Regenerate + Redo live in
            the header. Redo shares the reset panel above (audit reason recorded). */}
        {prep && (
          <div className="border-t border-border pt-3">
            <section className="bg-card border border-border rounded-xl overflow-hidden">
              <div
                className={[
                  "flex items-center gap-2.5 px-4 py-2 flex-wrap",
                  prepOpen || redoOpen || regenState === "error"
                    ? "border-b border-border"
                    : "",
                ].join(" ")}
              >
                <button
                  type="button"
                  aria-expanded={prepOpen}
                  onClick={() => setPrepOpen((o) => !o)}
                  className="flex items-center gap-2.5 flex-1 min-w-0 py-0.5 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
                >
                  <Brain className="h-4 w-4 text-primary shrink-0" />
                  <h3 className="text-[13px] font-semibold text-foreground">
                    {t("patientDetail.prepNoteSection")}
                  </h3>
                </button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={regenState === "running"}
                  onClick={() => void regenerate()}
                >
                  {regenState === "running" ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <RefreshCw className="h-3.5 w-3.5" />
                  )}
                  {regenState === "running"
                    ? t("patientDetail.regenerating")
                    : t("patientDetail.regeneratePrepNote")}
                </Button>
                {canRedo && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setRedoOpen((o) => !o)}
                  >
                    <RotateCcw className="h-3.5 w-3.5" />
                    {t("patientDetail.redoIntake", "Redo intake")}
                  </Button>
                )}
                <button
                  type="button"
                  aria-expanded={prepOpen}
                  aria-label={t("patientDetail.prepNoteSection")}
                  onClick={() => setPrepOpen((o) => !o)}
                  className="inline-flex items-center rounded-md p-1 text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <ChevronDown
                    className={[
                      "h-4 w-4 transition-transform",
                      prepOpen ? "rotate-180" : "",
                    ].join(" ")}
                    aria-hidden
                  />
                </button>
              </div>
            {regenState === "error" && (
              <span className="mx-4 mt-3 inline-flex items-center gap-1 text-xs text-destructive">
                <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                {t("patientDetail.regenerateError")}
              </span>
            )}
            {prepOpen && (
              <div className="px-4 py-3">
                <PrepNoteView
                  prepNote={prep}
                  patientName={patient.name}
                  prepStatus={prepStatus}
                  onReview={onReview}
                />
              </div>
            )}
            </section>
          </div>
        )}

        {/* Prep note pending — intake reached prep_ready/reviewed but no note yet */}
        {prepPending && (
          <div className="border-t border-border pt-3">
            <div className="flex flex-col gap-2.5 rounded-lg bg-secondary/60 border border-border px-3 py-2.5">
              <div className="flex items-center gap-2.5">
                <Brain className="h-4 w-4 text-muted-foreground shrink-0" />
                <div className="min-w-0">
                  <p className="text-[13px] font-medium text-foreground">
                    {t("patientDetail.prepNotePending")}
                  </p>
                  <p className="text-[11px] text-muted-foreground">
                    {t("patientDetail.prepNotePendingBody")}
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={regenState === "running"}
                  onClick={() => void regenerate()}
                >
                  {regenState === "running" ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <RefreshCw className="h-3.5 w-3.5" />
                  )}
                  {regenState === "running"
                    ? t("patientDetail.regenerating")
                    : t("patientDetail.regeneratePrepNoteLong")}
                </Button>
                {canReset && (
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={redoState === "redoing"}
                    onClick={() => setRedoOpen((o) => !o)}
                  >
                    <RotateCcw className="h-3.5 w-3.5" />
                    {t("patientDetail.redoIntake", "Redo intake")}
                  </Button>
                )}
                {regenState === "error" && (
                  <span className="mt-2 flex items-center gap-1 text-xs text-destructive">
                    <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                    {t("patientDetail.regenerateError")}
                  </span>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Transcript — its own section card, separate from the prep note.
            Collapsed by default; TranscriptView mounts (and fetches) on expand. */}
        {(prep || prepPending || intakeStatus === "in_progress") && (
          <div className="border-t border-border pt-3">
            <Section
              icon={MessageSquare}
              title={t("patientDetail.transcriptSection")}
              count={0}
              collapsible
              defaultOpen={false}
            >
              <TranscriptView
                patientId={patient.id}
                interactionId={appt.interactionId}
              />
            </Section>
          </div>
        )}

        {/* Previous attempts — intakes redone for this encounter, retained as history */}
        {priorAttempts.length > 0 && (
          <div className="border-t border-border pt-3">
            <button
              onClick={() => setPriorOpen((o) => !o)}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
            >
              <RotateCcw className="h-3.5 w-3.5" />
              {t("patientDetail.priorAttempts", "Previous attempts")}
              <span className="font-mono tabular-nums opacity-70">
                {priorAttempts.length}
              </span>
              <ChevronDown
                className={[
                  "h-3.5 w-3.5 transition-transform",
                  priorOpen ? "rotate-180" : "",
                ].join(" ")}
                aria-hidden
              />
            </button>
            {priorOpen && (
              <ul className="mt-2 flex flex-col gap-1.5">
                {priorAttempts.map((a) => (
                  <li
                    key={a.interactionId}
                    className="rounded-md bg-secondary/50 border border-border px-2.5 py-1.5 text-[12px]"
                  >
                    <span className="text-foreground">
                      {t("patientDetail.priorAttemptRedone", "Redone")}{" "}
                      {formatDisplayDateTime(a.supersededAt)}
                    </span>
                    {a.reason && (
                      <span className="text-muted-foreground">
                        {" "}
                        — {a.reason}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
  );
}

// One encounter card — the anchor. Header carries the clinic-timezone slot, visit
// type, and status/intake pills; AppointmentBody carries the rest. Used by
// EncounterDetailPage and the narrow-viewport Encounters list; wide viewports use
// the tabular AppointmentRow instead.
export function AppointmentCard({
  appt,
  patient,
  prepStatus,
  defaultLanguage,
  onReview,
  onRefresh,
  onOpen,
  defaultShowKiosk = false,
  showSummary = true,
}: {
  appt: PatientAppointment;
  patient: PatientDetail;
  prepStatus: PatientDetail["prepStatus"];
  defaultLanguage: KioskLang;
  onReview: () => void;
  onRefresh: () => void;
  onOpen?: () => void;
  defaultShowKiosk?: boolean;
  showSummary?: boolean;
}) {
  const { t } = useTranslation();
  const [intakeStatus, setIntakeStatus] = useState<IntakeStatus>(
    appt.intakeStatus,
  );
  // Reconcile with fresh server data: the parent refetches on a poll, so adopt the latest
  // intake status from props. Without this the card froze at its mount-time value and never
  // reflected a completed intake / landed note until a manual reload. Optimistic local
  // updates (cancel/send) are superseded once the real server value lands.
  useEffect(() => {
    setIntakeStatus(appt.intakeStatus);
  }, [appt.intakeStatus]);

  const slot = formatApptTime(appt.scheduledAt, appt.clinicTimezone);
  const typeLabel = apptTypeLabel(t, appt);

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden">
      {/* Header: slot + visit type · status + intake pills */}
      <div className="flex flex-wrap items-start justify-between gap-3 px-4 py-3 border-b border-border">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-foreground">
            {slot.date}
            {slot.date && slot.time ? " · " : ""}
            {slot.time}
            {typeLabel ? (
              <span className="text-muted-foreground font-normal">
                {" "}
                · {typeLabel}
              </span>
            ) : null}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <ApptStatusPill status={appt.status} />
          <IntakeStatusPill status={intakeStatus} />
          {onOpen && (
            <button
              type="button"
              onClick={onOpen}
              className="inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[11px] font-medium text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
            >
              {t("patientDetail.openEncounter", "Open")}
              <ChevronRight className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>

      <AppointmentBody
        appt={appt}
        patient={patient}
        prepStatus={prepStatus}
        defaultLanguage={defaultLanguage}
        onReview={onReview}
        onRefresh={onRefresh}
        intakeStatus={intakeStatus}
        setIntakeStatus={setIntakeStatus}
        showSummary={showSummary}
        defaultShowKiosk={defaultShowKiosk}
      />
    </div>
  );
}

// One visit as a table row; expanding it reveals the full AppointmentBody in a
// colSpan detail row, so the tabular view loses nothing the cards had.
function AppointmentRow({
  appt,
  patient,
  prepStatus,
  defaultLanguage,
  onReview,
  onRefresh,
  onOpen,
}: {
  appt: PatientAppointment;
  patient: PatientDetail;
  prepStatus: PatientDetail["prepStatus"];
  defaultLanguage: KioskLang;
  onReview: () => void;
  onRefresh: () => void;
  onOpen?: () => void;
}) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [expanded, setExpanded] = useState(false);
  const [intakeStatus, setIntakeStatus] = useState<IntakeStatus>(
    appt.intakeStatus,
  );
  // Same server reconcile as AppointmentCard — see the comment there.
  useEffect(() => {
    setIntakeStatus(appt.intakeStatus);
  }, [appt.intakeStatus]);

  const slot = formatApptTime(appt.scheduledAt, appt.clinicTimezone);
  const typeLabel = apptTypeLabel(t, appt);

  // A not_sent row has nothing to reveal — Start Intake goes straight to the
  // encounter page — so it carries no expand affordance at all.
  const expandable = intakeStatus !== "not_sent";

  // Per-status quick action in the Action column: not_sent → open the
  // encounter page where intake is launched (same as the Visits worklist);
  // in_progress → redo (supersede + restart); prep_ready → regenerate the note.
  const [quickState, setQuickState] = useState<"idle" | "busy" | "error">(
    "idle",
  );
  async function runQuick(fn: () => Promise<unknown>) {
    setQuickState("busy");
    try {
      await fn();
      setQuickState("idle");
      onRefresh();
    } catch {
      setQuickState("error");
    }
  }
  const quickActor =
    user?.displayName?.trim() || user?.email?.trim() || "unknown user";
  const quickAction =
    intakeStatus === "not_sent" && onOpen
      ? {
          label: t("visits.startIntake"),
          icon: Tablet,
          run: onOpen,
        }
      : intakeStatus === "link_sent" && onOpen
        ? {
            label: t(intakeActionLabelKey(intakeStatus)),
            icon: Send,
            run: onOpen,
          }
      : intakeStatus === "in_progress" && appt.appointmentId
        ? {
            label: t("patientDetail.redoIntake", "Redo intake"),
            icon: RotateCcw,
            run: () =>
              void runQuick(async () => {
                await resetAppointmentIntake(appt.appointmentId, quickActor);
                setIntakeStatus("not_sent");
              }),
          }
        : intakeStatus === "prep_ready"
          ? {
              label: t("patientDetail.regeneratePrepNote"),
              icon: RefreshCw,
              run: () => void runQuick(() => regeneratePrepNote(patient.id)),
            }
          : null;

  return (
    <>
      <tr
        className={[
          "align-top transition-colors",
          expandable ? "cursor-pointer hover:bg-secondary/40" : "",
          expanded ? "bg-secondary/30" : "border-b border-border last:border-0",
        ].join(" ")}
        onClick={() => {
          if (expandable) setExpanded((e) => !e);
        }}
      >
        <td className="px-3 py-2.5 whitespace-nowrap">
          <span className="text-[13px] font-semibold text-foreground">
            {slot.date}
          </span>
          {slot.time && (
            <span className="block font-mono text-[11px] text-muted-foreground">
              {slot.time}
            </span>
          )}
        </td>
        <td className="px-3 py-2.5 text-foreground">{typeLabel ?? DASH}</td>
        <td className="px-3 py-2.5 text-foreground">{appt.reason || DASH}</td>
        <td className="px-3 py-2.5 text-foreground">
          {appt.providerName || DASH}
        </td>
        <td className="px-3 py-2.5">
          <ApptStatusPill status={appt.status} />
        </td>
        <td className="px-3 py-2.5">
          <IntakeStatusPill status={intakeStatus} />
        </td>
        <td className="px-3 py-2.5">
          <div className="flex items-center justify-end gap-1.5">
            {quickState === "error" && (
              <AlertTriangle
                className="h-3.5 w-3.5 text-destructive shrink-0"
                aria-label={t("patientDetail.retry")}
              />
            )}
            {quickAction && (
              <Button
                size="sm"
                variant="outline"
                className="h-8 w-28 justify-start"
                disabled={quickState === "busy"}
                onClick={(e) => {
                  e.stopPropagation();
                  quickAction.run();
                }}
              >
                {quickState === "busy" ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <quickAction.icon className="h-3.5 w-3.5" aria-hidden />
                )}
                {quickAction.label}
              </Button>
            )}
            {expandable ? (
              <button
                type="button"
                aria-expanded={expanded}
                aria-label={t(
                  "patientDetail.toggleEncounterDetails",
                  "Details",
                )}
                onClick={(e) => {
                  e.stopPropagation();
                  setExpanded((v) => !v);
                }}
                className="inline-flex items-center rounded-md p-1 text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
              >
                <ChevronDown
                  className={[
                    "h-4 w-4 transition-transform",
                    expanded ? "rotate-180" : "",
                  ].join(" ")}
                  aria-hidden
                />
              </button>
            ) : (
              // Reserve the chevron's slot so buttons stay column-aligned
              // across rows with and without the expand control.
              <span className="w-6 shrink-0" aria-hidden />
            )}
          </div>
        </td>
      </tr>
      {expanded && (
        <tr className="border-b border-border last:border-0">
          <td colSpan={7} className="p-0 bg-secondary/[0.15]">
            <AppointmentBody
              appt={appt}
              patient={patient}
              prepStatus={prepStatus}
              defaultLanguage={defaultLanguage}
              onReview={onReview}
              onRefresh={onRefresh}
              intakeStatus={intakeStatus}
              setIntakeStatus={setIntakeStatus}
              showSummary={false}
            />
          </td>
        </tr>
      )}
    </>
  );
}

// Tracks a min-width media query; false where matchMedia is unavailable (jsdom).
function useMinWidth(px: number): boolean {
  const [matches, setMatches] = useState(
    () =>
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia(`(min-width: ${px}px)`).matches,
  );
  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const mql = window.matchMedia(`(min-width: ${px}px)`);
    const onChange = (e: MediaQueryListEvent) => setMatches(e.matches);
    setMatches(mql.matches);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, [px]);
  return matches;
}

// Encounters tab — the anchor. One card per appointment; each card renders that
// encounter's OWN prep note (appt.prepNote), never a guessed patient-level note.
function EncountersTab({
  appointments,
  patient,
  prepStatus,
  defaultLanguage,
  onReview,
  onRefresh,
}: {
  appointments: PatientAppointment[];
  patient: PatientDetail;
  prepStatus: PatientDetail["prepStatus"];
  defaultLanguage: KioskLang;
  onReview: () => void;
  onRefresh: () => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // Use cards through tablet portrait widths; the dense table is better suited
  // to iPad landscape and desktop where columns have enough room.
  const wide = useMinWidth(1024);
  const sorted = [...appointments].sort((a, b) =>
    (b.scheduledAt ?? "").localeCompare(a.scheduledAt ?? ""),
  );

  if (sorted.length === 0) {
    return (
      <div className="flex flex-col items-center gap-3 py-16 text-center">
        <div className="h-12 w-12 rounded-xl bg-secondary flex items-center justify-center">
          <CalendarClock className="h-6 w-6 text-muted-foreground" />
        </div>
        <p className="text-sm text-muted-foreground">
          {t("patientDetail.noAppointments")}
        </p>
      </div>
    );
  }

  if (!wide) {
    return (
      <div className="flex flex-col gap-3">
        {sorted.map((appt) => (
          <AppointmentCard
            key={appt.appointmentId}
            appt={appt}
            patient={patient}
            prepStatus={prepStatus}
            defaultLanguage={defaultLanguage}
            onReview={onReview}
            onRefresh={onRefresh}
            onOpen={() =>
              navigate(
                `/patients/${patient.id}/encounters/${appt.appointmentId}`,
              )
            }
          />
        ))}
      </div>
    );
  }

  const headerCell =
    "font-mono text-[10px] font-semibold uppercase tracking-wider text-muted-foreground px-3 py-2.5";

  return (
    // Same card treatment as the Visits and Patients tables: gradient top strip.
    <div className="bg-card rounded-2xl border border-border shadow-sm overflow-hidden">
      <div className="h-1 bg-gradient-to-r from-[#006EF9] via-[#02C7C3] to-[#006EF9]" />
      <div className="overflow-x-auto">
        <table className="w-full min-w-[720px] table-fixed text-[13px]">
          <colgroup>
            <col className="w-[10%]" />
            <col className="w-[13%]" />
            <col className="w-[20%]" />
            <col className="w-[22%]" />
            <col className="w-[8%]" />
            <col className="w-[11%]" />
            <col className="w-[16%]" />
          </colgroup>
          <thead>
            <tr className="border-b border-border text-left bg-secondary/40">
              <th className={headerCell}>{t("visits.colTime")}</th>
              <th className={headerCell}>{t("visits.colVisitType")}</th>
              <th className={headerCell}>{t("visits.colReason")}</th>
              <th className={headerCell}>{t("visits.colProvider")}</th>
              <th className={headerCell}>{t("visits.colStatus")}</th>
              <th className={headerCell}>{t("visits.colIntakeStatus")}</th>
              <th className={[headerCell, "text-right"].join(" ")}>
                {t("visits.colAction")}
              </th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((appt) => (
              <AppointmentRow
                key={appt.appointmentId}
                appt={appt}
                patient={patient}
                prepStatus={prepStatus}
                defaultLanguage={defaultLanguage}
                onReview={onReview}
                onRefresh={onRefresh}
                onOpen={() =>
                  navigate(
                    `/patients/${patient.id}/encounters/${appt.appointmentId}`,
                  )
                }
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const PULL_STATUS_TONE: Record<PullStatus, string> = {
  completed: "bg-emerald-500/12 text-emerald-700 dark:text-emerald-300",
  no_data: "bg-secondary text-muted-foreground",
  skipped_fresh: "bg-secondary text-muted-foreground",
  failed: "bg-red-500/12 text-red-600 dark:text-red-400",
  running: "bg-blue-500/12 text-blue-700 dark:text-blue-300",
  pending: "bg-amber-500/12 text-amber-700 dark:text-amber-300",
};

// Drawer: every HIE pull attempt for this patient, most-recent-first.
export function ClinicalPullsDrawer({
  patientId,
  open,
  onClose,
}: {
  patientId: string;
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [pulls, setPulls] = useState<ApiClinicalPull[] | null>(null);
  const [state, setState] = useState<"loading" | "done" | "error">("loading");

  const statusLabel = (s: PullStatus) => t(`patientDetail.status_${s}`);
  const triggerLabel = (s: TriggerSource) => t(`patientDetail.trigger_${s}`);

  useEffect(() => {
    if (!open) return;
    let active = true;
    setState("loading");
    setPulls(null);
    (async () => {
      try {
        const res = await request<{ pulls: ApiClinicalPull[] }>(
          `/v1/patients/${encodeURIComponent(patientId)}/clinical-pulls`,
        );
        if (!active) return;
        setPulls(res.pulls ?? []);
        setState("done");
      } catch {
        if (active) setState("error");
      }
    })();
    return () => {
      active = false;
    };
  }, [open, patientId]);

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
    >
      <DialogContent
        title={t("patientDetail.historyTitle")}
        className="max-w-2xl"
      >
        {state === "loading" && (
          <div className="flex items-center gap-2 py-10 justify-center text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            {t("patientDetail.historyLoading")}
          </div>
        )}
        {state === "error" && (
          <p className="text-sm text-destructive py-10 text-center">
            {t("patientDetail.historyError")}
          </p>
        )}
        {state === "done" && pulls && pulls.length === 0 && (
          <div className="flex flex-col items-center gap-3 py-12 text-center">
            <div className="h-12 w-12 rounded-xl bg-secondary flex items-center justify-center">
              <History className="h-6 w-6 text-muted-foreground" />
            </div>
            <p className="text-sm text-muted-foreground">
              {t("patientDetail.historyEmpty")}
            </p>
          </div>
        )}
        {state === "done" && pulls && pulls.length > 0 && (
          <div className="overflow-x-auto -mx-2">
            <table className="w-full min-w-[520px] text-[13px]">
              <thead>
                <tr className="border-b border-border text-left">
                  <th className="font-mono text-[10px] font-semibold uppercase tracking-wider text-muted-foreground px-2 pb-2">
                    {t("patientDetail.historyColTime")}
                  </th>
                  <th className="font-mono text-[10px] font-semibold uppercase tracking-wider text-muted-foreground px-2 pb-2">
                    {t("patientDetail.historyColTrigger")}
                  </th>
                  <th className="font-mono text-[10px] font-semibold uppercase tracking-wider text-muted-foreground px-2 pb-2">
                    {t("patientDetail.historyColStatus")}
                  </th>
                  <th className="font-mono text-[10px] font-semibold uppercase tracking-wider text-muted-foreground px-2 pb-2 text-right">
                    {t("patientDetail.historyColRecords")}
                  </th>
                  <th className="font-mono text-[10px] font-semibold uppercase tracking-wider text-muted-foreground px-2 pb-2">
                    {t("patientDetail.historyColReason")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {pulls.map((p) => (
                  <tr
                    key={p.id}
                    className="border-b border-border last:border-0 align-top"
                  >
                    <td className="px-2 py-2.5 font-mono text-[12px] text-muted-foreground whitespace-nowrap">
                      {formatAgo(t, p.startedAt) ?? p.startedAt}
                    </td>
                    <td className="px-2 py-2.5 text-foreground">
                      {triggerLabel(p.triggerSource)}
                    </td>
                    <td className="px-2 py-2.5">
                      <span
                        className={[
                          "text-[11px] font-medium px-2 py-0.5 rounded-full",
                          PULL_STATUS_TONE[p.status],
                        ].join(" ")}
                      >
                        {statusLabel(p.status)}
                      </span>
                    </td>
                    <td className="px-2 py-2.5 font-mono tabular-nums text-right text-foreground">
                      {p.fileCount}
                    </td>
                    <td className="px-2 py-2.5 text-muted-foreground">
                      {p.errorMessage || p.errorCode || DASH}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// Live "instrument readout" chip for the patient's HIE sync state.
function SyncChip({
  latestPull,
  clinicalDataUpdatedAt,
  syncing,
  catchingUp,
}: {
  latestPull?: ApiLatestPull | null;
  clinicalDataUpdatedAt?: string | null;
  syncing: boolean;
  catchingUp?: boolean;
}) {
  const { t } = useTranslation();

  let tone = "bg-secondary text-muted-foreground";
  let dot = "bg-muted-foreground/50";
  let text: React.ReactNode = t("patientDetail.chipNever");
  let spin = false;

  // catchingUp = the pull completed but the records land async (Pub/Sub); stay busy
  // (not the green "Synced") until they surface, so the chip never lies about state.
  if (
    syncing ||
    catchingUp ||
    latestPull?.status === "running" ||
    latestPull?.status === "pending"
  ) {
    tone = "bg-blue-500/10 text-blue-700 dark:text-blue-300";
    dot = "bg-blue-500";
    text =
      catchingUp && !syncing
        ? t("patientDetail.chipFetchingRecords")
        : t("patientDetail.chipSyncing");
    spin = true;
  } else if (latestPull) {
    switch (latestPull.status) {
      case "completed": {
        const ago = formatAgo(t, latestPull.finishedAt ?? latestPull.startedAt);
        tone = "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300";
        dot = "bg-emerald-500";
        text = `${t("patientDetail.chipSynced", { time: ago })} · ${t("patientDetail.chipRecords", { count: latestPull.fileCount })}`;
        break;
      }
      case "no_data":
        text = t("patientDetail.chipNoData");
        break;
      case "skipped_fresh":
        tone = "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300";
        dot = "bg-emerald-500";
        text = t("patientDetail.chipUpToDate");
        break;
      case "failed":
        tone = "bg-red-500/10 text-red-600 dark:text-red-400";
        dot = "bg-red-500";
        text = t("patientDetail.chipFailed");
        break;
    }
  } else if (clinicalDataUpdatedAt) {
    tone = "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300";
    dot = "bg-emerald-500";
    text = t("patientDetail.chipSynced", {
      time: formatAgo(t, clinicalDataUpdatedAt),
    });
  }

  return (
    <span
      className={[
        "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 font-mono text-[11px]",
        tone,
      ].join(" ")}
    >
      {spin ? (
        <Loader2 className="h-3 w-3 animate-spin" />
      ) : (
        <span
          className={["h-1.5 w-1.5 rounded-full", dot].join(" ")}
          aria-hidden
        />
      )}
      {text}
    </span>
  );
}

export function PatientHeader({
  patient,
  apiPatient,
  prepStatus,
  syncing,
  catchingUp,
  onRefreshRecords,
  onOpenHistory,
  outcomeContent,
}: {
  patient: PatientDetail;
  apiPatient: ApiPatientDetail;
  prepStatus: PatientDetail["prepStatus"];
  syncing: boolean;
  catchingUp?: boolean;
  onRefreshRecords: () => void;
  onOpenHistory: () => void;
  outcomeContent?: React.ReactNode;
}) {
  const { t } = useTranslation();
  const sex = titleCase(apiPatient.sex ?? apiPatient.gender);
  const age = ageFromDob(apiPatient.dateOfBirth);
  const vitals: { label: string; value: string; mono?: boolean }[] = [
    { label: t("patientDetail.labelDob"), value: patient.dob, mono: true },
  ];
  if (age !== null)
    vitals.push({
      label: t("patientDetail.labelAge"),
      value: t("patientDetail.yearsOld", { count: age }),
    });
  if (sex) vitals.push({ label: t("patientDetail.labelSex"), value: sex });
  if (apiPatient.mrn)
    vitals.push({
      label: t("patientDetail.labelMrn"),
      value: apiPatient.mrn,
      mono: true,
    });

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden flex">
      <span
        aria-hidden
        className="w-1.5 shrink-0 bg-gradient-to-b from-primary to-accent"
      />
      <div className="flex-1 min-w-0 p-4 sm:p-5 flex flex-col gap-4">
        <div className="flex items-start gap-3.5">
          <PersonAvatar seed={patient.id} size={52} ringClassName="ring-border" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2.5 flex-wrap">
              <h1 className="text-xl sm:text-2xl font-bold text-foreground leading-tight">
                {patient.name}
              </h1>
              <Badge variant={prepStatus}>{PREP_STATUS_LABEL[prepStatus]}</Badge>
            </div>
            <div className="flex items-center flex-wrap gap-x-3 gap-y-1 mt-1.5">
              {vitals.map((v, i) => (
                <React.Fragment key={v.label}>
                  {i > 0 && (
                    <span className="text-border" aria-hidden>
                      ·
                    </span>
                  )}
                  <span className="text-[12px] text-muted-foreground">
                    <span className="uppercase tracking-wide text-[10px] mr-1">
                      {v.label}
                    </span>
                    <span
                      className={[
                        "text-foreground",
                        v.mono ? "font-mono" : "font-medium",
                      ].join(" ")}
                    >
                      {v.value}
                    </span>
                  </span>
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>

        <div className="flex items-center flex-wrap gap-2.5 pt-1">
          <SyncChip
            latestPull={apiPatient.latestPull}
            clinicalDataUpdatedAt={patient.clinicalDataUpdatedAt}
            syncing={syncing}
            catchingUp={catchingUp}
          />
          <button
            onClick={onRefreshRecords}
            disabled={syncing || catchingUp}
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary hover:underline disabled:opacity-60 disabled:cursor-not-allowed disabled:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
          >
            {syncing || catchingUp ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <RefreshCw className="h-3.5 w-3.5" />
            )}
            {t("patientDetail.refreshRecords")}
          </button>
          <button
            onClick={onOpenHistory}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
          >
            <History className="h-3.5 w-3.5" />
            {t("patientDetail.historyButton")}
          </button>
        </div>

        {outcomeContent}
      </div>
    </div>
  );
}

export default function PatientDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { updatePatient } = usePatients();
  const { role } = useAuth();
  const clinicalStaffOnly = isClinicalStaff(role);
  const canViewSnapshot = !clinicalStaffOnly;

  // The Visits worklist deep-links into a tab via ?tab=encounters|snapshot.
  const initialTab = searchParams.get("tab");
  const [view, setView] = useState<ViewKey>(
    clinicalStaffOnly || initialTab === "encounters"
      ? "encounters"
      : initialTab === "snapshot" && canViewSnapshot
        ? "snapshot"
        : "chart",
  );
  const [localPrepStatus, setLocalPrepStatus] = useState<"reviewed" | null>(
    null,
  );
  const [historyOpen, setHistoryOpen] = useState(false);

  // One shared hook owns the fetch + ALL eventual-consistency catch-up polling (clinical
  // pull + prep-note settling), so this page and EncounterDetailPage stay in sync without
  // a manual reload. See hooks/use-patient-detail.
  const {
    patient,
    apiPatient,
    fetchStatus,
    fetchError,
    reload,
    refreshRecords,
    syncing,
    catchingUp,
    outcome,
  } = usePatientDetail(id);

  useEffect(() => {
    if (clinicalStaffOnly && view !== "encounters") setView("encounters");
    else if (!canViewSnapshot && view === "snapshot") setView("chart");
  }, [canViewSnapshot, clinicalStaffOnly, view]);

  if (fetchStatus === "loading") {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-20 text-center">
        <span className="h-10 w-10 rounded-full border-2 border-primary/30 border-t-primary animate-spin-ring" />
        <p className="text-sm text-muted-foreground">
          {t("patientDetail.loading", "Loading patient…")}
        </p>
      </div>
    );
  }

  if (fetchStatus === "error") {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-20 text-center">
        <div className="h-16 w-16 rounded-2xl bg-destructive/10 flex items-center justify-center">
          <AlertTriangle className="h-8 w-8 text-destructive" />
        </div>
        <div>
          <p className="text-xl font-bold text-foreground">
            {t("patientDetail.loadFailed", "Could not load patient")}
          </p>
          <p className="text-sm text-muted-foreground mt-1">{fetchError}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => navigate(-1)}>
            {t("patientDetail.goBack")}
          </Button>
          <Button onClick={() => reload()}>{t("patientDetail.retry")}</Button>
        </div>
      </div>
    );
  }

  if (fetchStatus === "notfound" || !patient || !apiPatient) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-20 text-center">
        <div className="h-16 w-16 rounded-2xl bg-secondary flex items-center justify-center">
          <User className="h-8 w-8 text-muted-foreground" />
        </div>
        <div>
          <p className="text-xl font-bold text-foreground">
            {t("patientDetail.patientNotFound")}
          </p>
          <p className="text-sm text-muted-foreground mt-1">
            {t("patientDetail.patientNotFoundBody", { id })}
          </p>
        </div>
        <Button variant="outline" onClick={() => navigate(-1)}>
          {t("patientDetail.goBack")}
        </Button>
      </div>
    );
  }

  const prepStatus = localPrepStatus ?? patient.prepStatus;

  const VIEWS: { key: ViewKey; label: string }[] = clinicalStaffOnly
    ? [{ key: "encounters", label: t("patientDetail.tabEncounters") }]
    : [
        { key: "chart", label: t("patientDetail.tabClinical") },
        { key: "encounters", label: t("patientDetail.tabEncounters") },
        ...(canViewSnapshot
          ? [{ key: "snapshot" as const, label: t("patientDetail.tabSnapshot") }]
          : []),
      ];

  // The SyncChip is the single readout for terminal HIE state (Synced · N / No
  // records / Up to date). The banner only carries what the chip cannot: an "already
  // fresh" acknowledgement of the explicit refresh, the soft timeout nudge, and a
  // failure's specific reason + Retry action.
  const outcomeBanner =
    outcome &&
    (() => {
      if (outcome.kind === "fresh")
        return {
          tone: "muted",
          icon: CheckCircle2,
          text: t("patientDetail.outcomeFresh"),
          retry: false,
        };
      if (outcome.kind === "timedout")
        return {
          tone: "amber",
          icon: Clock,
          text: t("patientDetail.outcomeTimedout"),
          retry: false,
        };
      if (outcome.kind === "failed") {
        const codeKey = `patientDetail.err_${outcome.errorCode ?? ""}`;
        const msg = outcome.errorCode
          ? t(codeKey, { defaultValue: t("patientDetail.err_generic") })
          : t("patientDetail.err_generic");
        return { tone: "red", icon: AlertTriangle, text: msg, retry: true };
      }
      return null;
    })();

  const outcomeToneClass: Record<string, string> = {
    emerald:
      "bg-emerald-500/10 border-emerald-500/20 text-emerald-700 dark:text-emerald-300",
    muted: "bg-secondary border-border text-muted-foreground",
    amber:
      "bg-amber-500/10 border-amber-500/20 text-amber-700 dark:text-amber-300",
    red: "bg-destructive/10 border-destructive/20 text-destructive",
  };

  return (
    <div className="flex flex-col gap-5 max-w-6xl">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2.5 text-sm">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1.5 text-muted-foreground hover:text-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
        >
          <ArrowLeft className="h-4 w-4" />
          {t("patientDetail.back")}
        </button>
        <span className="text-muted-foreground/40">/</span>
        <span className="font-medium text-foreground truncate">
          {patient.name}
        </span>
      </div>

      <PatientHeader
        patient={patient}
        apiPatient={apiPatient}
        prepStatus={prepStatus}
        syncing={syncing}
        catchingUp={catchingUp}
        onRefreshRecords={() => void refreshRecords()}
        onOpenHistory={() => setHistoryOpen(true)}
        outcomeContent={
          outcomeBanner ? (
            <div
              className={[
                "flex items-center gap-2 rounded-lg border px-3 py-2 text-[13px]",
                outcomeToneClass[outcomeBanner.tone],
              ].join(" ")}
            >
              <outcomeBanner.icon className="h-4 w-4 shrink-0" />
              <span className="flex-1">{outcomeBanner.text}</span>
              {outcomeBanner.retry && (
                <button
                  onClick={() => void refreshRecords()}
                  className="font-semibold underline hover:no-underline shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
                >
                  {t("patientDetail.retry")}
                </button>
              )}
            </div>
          ) : null
        }
      />

      {/* View switcher — restrained segmented control */}
      <div
        role="tablist"
        aria-label={t("patientDetail.viewSwitcher")}
        className="inline-flex items-center gap-1 bg-secondary border border-border rounded-xl p-1 self-start max-w-full overflow-x-auto"
      >
        {VIEWS.map((v) => (
          <button
            key={v.key}
            role="tab"
            aria-selected={view === v.key}
            onClick={() => setView(v.key)}
            className={[
              "px-3.5 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              view === v.key
                ? "bg-card text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground",
            ].join(" ")}
          >
            {v.label}
          </button>
        ))}
      </div>

      {/* View content */}
      <div
        key={view}
        className="pb-8 animate-fade-up"
        style={{ animationDuration: "250ms" }}
      >
        {view === "chart" && <ChartView patient={patient} api={apiPatient} />}
        {view === "encounters" && (
          <EncountersTab
            appointments={apiPatient.appointments ?? []}
            patient={patient}
            prepStatus={prepStatus}
            defaultLanguage={toKioskLang(apiPatient.language)}
            onReview={() => {
              setLocalPrepStatus("reviewed");
              updatePatient(patient.id, { prepStatus: "reviewed" });
            }}
            onRefresh={() => reload()}
          />
        )}
        {canViewSnapshot && view === "snapshot" && (
          <SnapshotView patientId={patient.id} />
        )}
      </div>

      <ClinicalPullsDrawer
        patientId={patient.id}
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
      />
    </div>
  );
}
